import Channel from '@@types/Channel';
import { CHANNEL_ID, ID, MEMBER_ID } from '@@types/common';

import { ChannelFlag, ChannelMemberRole, ChannelType, MAIN_THREAD_CHANNEL_ID, URLS } from '~/constants';
import i18n from '~/i18n';
import { isBotChannel, isMainThreadChannel, isMeChannel } from '~/services/channelService';
import Context from '~/stores/context';
import { memoize } from 'lodash';
import { always, cond, find, juxt, pipe, propEq, propOr, reduce, T, trim } from 'ramda';

const getLatestTime: (channel: Channel) => number = pipe(
  juxt([propOr<number>(0, 'updatedAt'), propOr<number>(0, 'archivedAt'), propOr<number>(0, 'displayedChangedAt')]),
  reduce((a: number, b = 0) => Math.max(a, b as number), 0)
);

const getOrderFactor = (channel: Channel) => {
  return channel.type + channel.shared + channel.updatedAt + channel.archivedAt + channel.displayedChangedAt;
};

export const calcChannelSortPriority = memoize((channel: Channel) => {
  return cond([
    [isBotChannel, always('E')],
    [isMainThreadChannel, always('D')],
    [isMeChannel, always('C')],
    [T, (channel: Channel) => `A-${getLatestTime(channel)}`],
  ])(channel);
}, getOrderFactor);

export const validateCapacity = (capacity: number, memberIds: MEMBER_ID[]): Promise<MEMBER_ID[]> => {
  return memberIds.length > capacity ? Promise.reject({ error: 'capacity', capacity }) : Promise.resolve(memberIds);
};

export const showCapacityAlert = (params: { error: string; capacity: number }): Promise<never> => {
  Context.actions.notificationActions.showError({
    text: Context.utils.i18n.t('CHANNEL_CREATE_INVALID_CAPACITY_ERROR', { max: params.capacity }),
  });
  return Promise.reject({ message: 'CHANNEL_CREATE_INVALID_CAPACITY_ERROR' });
};

export const validateDuplicateChannelTitle = (
  checkDuplicateTitle: (title: string) => Promise<void>,
  title: string,
  passes: (isSuccess?: boolean, message?: string) => void
) => {
  checkDuplicateTitle(trim(title)).then(
    () => passes(true),
    () => passes(false, i18n.t('topicChannel.existTitle', '이미 존재하는 대화방입니다. 다른 제목으로 지정해 주세요.'))
  );
};

export const getChannelImageUrl = (channelId: CHANNEL_ID, imageId: ID, isShared: boolean) => {
  return isShared
    ? `${URLS.V4_API}/channels/${channelId}/image?isThumb=true&${imageId}`
    : `${URLS.API}/channels/${channelId}/image?isThumb=true&${imageId}`;
};

export const makeThreadChannel = (): Channel => {
  return {
    channelId: MAIN_THREAD_CHANNEL_ID,
    archivedAt: null,
    capacity: 0,
    readSeq: 0,
    seq: 0,
    flag: ChannelFlag.Custom,
    translate: 0,
    title: i18n.t('thread.channel', '글타래(스레드)'),
    type: ChannelType.Custom,
    role: ChannelMemberRole.Member,
    management: null,
    displayed: true,
    displayedChangedAt: null,
    updatedAt: 0,
    unreadCount: 0,
    mentionCount: 0,
    startSeq: 0,
    orgId: '',
    createdAt: 0,
    shared: false,
    starred: false,
    status: 0,
    channelMemberNumber: 1,
  };
};

export const getChannelLangPreference = (channel: Channel) => {
  const langPreference = pipe<any, any, any>(propOr([], 'preferences'), find(propEq('category', 'lang')))(channel);

  return langPreference?.value?.lang;
};
